import { useTranslation } from "react-i18next"

type Translations = typeof defaultTranslations

type UseTranslationsParams = {
  sentAt?: string
  timezone?: string
}

const defaultTranslations = {
  notificationTimeLabel: "When to notify?",
  notificationTimeSummary: "Notification will be sent at",
  notificationExpiredSummary: "Notification is expired.",
  minutes: "minutes",
  hours: "hours",
  days: "days",
  weeks: "weeks",
  before: "before",
  beforeCalendarEvent: "before calendar event",
  after: "after",
  plannedStartDate: "planned start date",
  plannedEndDate: "planned end date",
  notificationRecipientsLabel: "Whom to notify?",
  notificationRecipientHelperText: "Notification requires at least one recipient.",
  roleRecipients: "Recipients by role",
  projectManager: "Project Manager",
  projectParticipants: "Project Participants",
  taskManager: "Task responsible",
  taskParticipants: "Task participants",
  managerOfParentTask: "Responsible of parent task",
  managersOfDependentTasks: "Responsible of dependent tasks",
  calendarEventParticipants: "Calendar event participants",
  todoResponsible: "To-do responsible",
  addNoteLabel: "Add a note",
  notStarted: "Not started",
  completed: "Completed",
  inProgress: "In progress",
  notificationStatusSummary: "Notification will be sent when status changes to {{status}}",
  addNotification: "Add notification",
  notificationsEmpty: "You have not created any notifications here",
  showOnlyActive: "Show only active notifications",
  expired: "Expired",
  sent: "Sent",
  notificationWasSentTo: "Notification was sent to",
  notificationWillBeSentTo: "Notification will be sent to",
  deleteNotification: "Delete notification",
  editNotification: "Edit notification",
  emailRecipients: "Email Recipients",
  addEmailRecipientLabel: "Add email recipient",
  errorUpdatingEmailRecipients: "Failed to update email recipients",
  errorUpdatingReminderTime: "Failed to update reminder time",
  add: "Add",
  cancel: "Cancel",
  sendNotification: "Send notification",
  at: "at",
  whenStatusChanges: "when status changes to",
  calendarEvent: "Calendar event",
  todo: "To-do",
  dueDate: "Due date",
  notificationTriggeredWithoutRecipients:
    "Notification was triggered @ {{sentAt}} ({{timezone}}), but did not have any recipients.",
}

export const useTranslations = ({ sentAt = "", timezone = "" }: UseTranslationsParams = {}): Translations => {
  const { t } = useTranslation()
  const { t: tProject } = useTranslation("project")

  return {
    notificationTimeLabel: t("notificationTimeLabel", { defaultValue: defaultTranslations.notificationTimeLabel }),
    notificationTimeSummary: t("notificationTimeSummary", {
      defaultValue: defaultTranslations.notificationTimeSummary,
    }),
    notificationStatusSummary: t("notificationStatusSummary", {
      defaultValue: defaultTranslations.notificationStatusSummary,
    }),
    notificationExpiredSummary: t("notificationExpiredSummary", {
      defaultValue: defaultTranslations.notificationExpiredSummary,
    }),
    minutes: t("minutes", { defaultValue: defaultTranslations.minutes }),
    hours: t("hours", { defaultValue: defaultTranslations.hours }),
    days: t("days", { defaultValue: defaultTranslations.days }),
    weeks: t("weeks", { defaultValue: defaultTranslations.weeks }),
    before: t("before", { defaultValue: defaultTranslations.before }),
    beforeCalendarEvent: t("beforeCalendarEvent", { defaultValue: defaultTranslations.beforeCalendarEvent }),
    after: t("after", { defaultValue: defaultTranslations.after }),
    plannedStartDate: t("plannedStartDate", { defaultValue: defaultTranslations.plannedStartDate }),
    plannedEndDate: t("plannedEndDate", { defaultValue: defaultTranslations.plannedEndDate }),
    notificationRecipientsLabel: t("notificationRecipientsLabel", {
      defaultValue: defaultTranslations.notificationRecipientsLabel,
    }),
    roleRecipients: t("roleRecipients", { defaultValue: defaultTranslations.roleRecipients }),
    projectManager: t("projectManager", { defaultValue: defaultTranslations.projectManager }),
    projectParticipants: t("projectParticipants", { defaultValue: defaultTranslations.projectParticipants }),
    taskManager: t("taskManager", { defaultValue: defaultTranslations.taskManager }),
    taskParticipants: t("taskParticipants", { defaultValue: defaultTranslations.taskParticipants }),
    managerOfParentTask: t("managerOfParentTask", { defaultValue: defaultTranslations.managerOfParentTask }),
    managersOfDependentTasks: t("managersOfDependentTasks", {
      defaultValue: defaultTranslations.managersOfDependentTasks,
    }),
    calendarEventParticipants: t("calendarEventParticipants", {
      defaultValue: defaultTranslations.calendarEventParticipants,
    }),
    todoResponsible: t("todoResponsible", { defaultValue: defaultTranslations.todoResponsible }),
    addNoteLabel: t("addNoteLabel", { defaultValue: defaultTranslations.addNoteLabel }),
    notificationRecipientHelperText: t("notificationRecipientHelperText", {
      defaultValue: defaultTranslations.notificationRecipientHelperText,
    }),
    addNotification: t("addNotification", { defaultValue: defaultTranslations.addNotification }),
    notificationsEmpty: t("notificationsEmpty", { defaultValue: defaultTranslations.notificationsEmpty }),
    showOnlyActive: t("showOnlyActive", { defaultValue: defaultTranslations.showOnlyActive }),
    expired: t("expired", { defaultValue: defaultTranslations.expired }),
    sent: t("sent", { defaultValue: defaultTranslations.sent }),
    notificationWasSentTo: t("notificationWasSentTo", {
      defaultValue: defaultTranslations.notificationWasSentTo,
    }),
    notificationWillBeSentTo: t("notificationWillBeSentTo", {
      defaultValue: defaultTranslations.notificationWillBeSentTo,
    }),
    deleteNotification: t("deleteNotification", { defaultValue: defaultTranslations.deleteNotification }),
    editNotification: t("editNotification", { defaultValue: defaultTranslations.editNotification }),
    emailRecipients: t("emailRecipients", { defaultValue: defaultTranslations.emailRecipients }),
    addEmailRecipientLabel: t("addEmailRecipientLabel", { defaultValue: defaultTranslations.addEmailRecipientLabel }),
    errorUpdatingEmailRecipients: t("errorUpdatingEmailRecipients", {
      defaultValue: defaultTranslations.errorUpdatingEmailRecipients,
    }),
    errorUpdatingReminderTime: t("errorUpdatingReminderTime", {
      defaultValue: defaultTranslations.errorUpdatingReminderTime,
    }),
    add: t("add", { defaultValue: defaultTranslations.add }),
    cancel: t("cancel", { defaultValue: defaultTranslations.cancel }),
    sendNotification: t("sendNotification", { defaultValue: defaultTranslations.sendNotification }),
    at: t("at", { defaultValue: defaultTranslations.at }),
    whenStatusChanges: t("whenStatusChanges", { defaultValue: defaultTranslations.whenStatusChanges }),
    calendarEvent: t("calendarEvent", { defaultValue: defaultTranslations.calendarEvent }),
    todo: t("todo", { defaultValue: defaultTranslations.todo }),
    dueDate: t("dueDate", { defaultValue: defaultTranslations.dueDate }),
    notStarted: tProject("projectStatusMenu.notStarted", { defaultValue: defaultTranslations.notStarted }),
    completed: tProject("projectStatusMenu.completed", { defaultValue: defaultTranslations.completed }),
    inProgress: tProject("projectStatusMenu.inProgress", { defaultValue: defaultTranslations.inProgress }),
    notificationTriggeredWithoutRecipients: t("notificationTriggeredWithoutRecipients", {
      defaultValue: defaultTranslations.notificationTriggeredWithoutRecipients,
      sentAt,
      timezone,
    }),
  }
}
